/**
 * The arithmetic the back-office settings form does between what the owner
 * types and what the API stores, kept out of the components so it can be
 * tested in `node` like the rest of `lib/`.
 *
 * The API stores rates in basis points (975 is 9.75%) so that no rate is ever
 * a float on the wire; a person types percentages. Both directions live here.
 */

/** 975 → "9.75"; 1000 → "10". Null is a blank field: "no rate", not 0%. */
export function percentFromBps(bps: number | null | undefined): string {
  if (bps === null || bps === undefined) return ''
  return String(Number((bps / 100).toFixed(2)))
}

/**
 * What the owner typed, as basis points — `null` for a blank field, and
 * `undefined` for something that is not a percentage from 0 to 100 with at
 * most two decimals, which the form refuses before sending.
 */
export function bpsFromPercent(raw: string): number | null | undefined {
  const text = raw.trim().replace(/%$/, '').trim()
  if (text === '') return null
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return undefined
  const bps = Math.round(Number(text) * 100)
  return bps >= 0 && bps <= 10_000 ? bps : undefined
}

/**
 * "10, 11:30am, 2pm, 15:00" → ["10:00", "11:30", "14:00", "15:00"]: the
 * boutique's usual start times, sorted and once each. A bare hour from 1 to 7
 * is read as the afternoon, because nobody books a fitting at 3 in the
 * morning. `undefined` when any part is not a time.
 */
export function timeList(raw: string): string[] | undefined {
  const parts = raw
    .split(',')
    .map((part) => part.trim().toLowerCase().replace(/\s+/g, ''))
    .filter(Boolean)
  const out = new Set<string>()
  for (const part of parts) {
    const match = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/.exec(part)
    if (!match) return undefined
    let hour = Number(match[1])
    const minute = Number(match[2] ?? '0')
    const meridiem = match[3]
    if (minute > 59) return undefined
    if (meridiem) {
      if (hour < 1 || hour > 12) return undefined
      hour = (hour % 12) + (meridiem === 'pm' ? 12 : 0)
    } else if (!match[2] && hour >= 1 && hour <= 7) {
      hour += 12
    }
    if (hour > 23) return undefined
    out.add(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
  }
  return [...out].sort()
}

/** "5, 10, 15" → [5, 10, 15]; `undefined` when any part is not a percentage. */
export function percentList(raw: string): number[] | undefined {
  const parts = raw
    .split(',')
    .map((part) => part.trim().replace(/%$/, ''))
    .filter(Boolean)
  const values = parts.map(Number)
  return values.every((v) => Number.isFinite(v) && v >= 0 && v <= 100) ? values : undefined
}

/**
 * A stable key for a new pick-list entry, from its label.
 *
 * The key is what an appointment stores, so it is made once — when the entry
 * is added — and never follows a later rename: renaming "IG" to "Instagram"
 * must relabel the appointments that chose it, not orphan them. A key already
 * on the list gets a numeric suffix.
 */
export function keyForLabel(label: string, taken: Iterable<string>): string {
  const base =
    label
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 56) || 'entry'
  const used = new Set(taken)
  if (!used.has(base)) return base
  let n = 2
  while (used.has(`${base}_${n}`)) n++
  return `${base}_${n}`
}
